import React, { useEffect, useState } from "react";
import AdminLayout from '@/components/layouts/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MessageCircle, Phone, Mail, User, ChevronDown, ChevronUp, Clock } from 'lucide-react';
import axios from "axios";

interface ChatMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
}

interface Conversation {
  id: number;
  session_id: string;
  visitor_name: string | null;
  visitor_email: string | null;
  visitor_phone: string | null;
  status: string;
  created_at: string;
  messages: ChatMessage[];
}

const ChatConversations = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [filter, setFilter] = useState<'all' | 'with_contact'>('all');

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    axios.get('https://universelle.ma/api/admin/chatbot/conversations', {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      setConversations(res.data.data || []);
    }).catch(err => {
      console.error(err);
    }).finally(() => setLoading(false));
  }, []);

  const filtered = conversations.filter(c => {
    if (filter === 'with_contact') return c.visitor_phone || c.visitor_email || c.visitor_name;
    return true;
  });

  const hasContact = (c: Conversation) => c.visitor_phone || c.visitor_email || c.visitor_name;

  const formatDate = (d: string) => new Date(d).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });

  const totalMessages = conversations.reduce((acc, c) => acc + c.messages.length, 0);
  const withContact = conversations.filter(hasContact).length;

  return (
    <AdminLayout>
      <div className="p-6 space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <MessageCircle className="text-yellow-600" size={28} />
              Conversations Chatbot IA
            </h1>
            <p className="text-gray-500 text-sm mt-1">Toutes les conversations des visiteurs avec le chatbot</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-l-4 border-l-yellow-500">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="bg-yellow-100 p-3 rounded-full">
                <MessageCircle className="text-yellow-600" size={22} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total conversations</p>
                <p className="text-2xl font-bold">{conversations.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="bg-green-100 p-3 rounded-full">
                <Phone className="text-green-600" size={22} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Avec coordonnées</p>
                <p className="text-2xl font-bold">{withContact}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="bg-blue-100 p-3 rounded-full">
                <MessageCircle className="text-blue-600" size={22} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total messages</p>
                <p className="text-2xl font-bold">{totalMessages}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filtres */}
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${filter === 'all' ? 'bg-yellow-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Toutes ({conversations.length})
          </button>
          <button
            onClick={() => setFilter('with_contact')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${filter === 'with_contact' ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Avec coordonnées ({withContact})
          </button>
        </div>

        {/* Liste conversations */}
        {loading ? (
          <div className="text-center py-20 text-gray-400">Chargement...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <MessageCircle size={48} className="mx-auto mb-4 opacity-30" />
            <p>Aucune conversation pour l'instant</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(conv => (
              <Card key={conv.id} className={`border ${hasContact(conv) ? 'border-green-200 bg-green-50/30' : 'border-gray-200'}`}>
                <CardContent className="p-0">
                  {/* Header conversation */}
                  <div
                    className="p-4 cursor-pointer flex items-center justify-between hover:bg-gray-50 transition-colors"
                    onClick={() => setExpanded(expanded === conv.id ? null : conv.id)}
                  >
                    <div className="flex items-center gap-4">
                      {/* Avatar */}
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${hasContact(conv) ? 'bg-green-100' : 'bg-gray-100'}`}>
                        <User size={18} className={hasContact(conv) ? 'text-green-600' : 'text-gray-400'} />
                      </div>
                      {/* Infos */}
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-800">
                            {conv.visitor_name || 'Visiteur anonyme'}
                          </span>
                          {hasContact(conv) && (
                            <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full font-medium">
                              Contact disponible
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                          {conv.visitor_phone && (
                            <span className="flex items-center gap-1 text-sm text-blue-600 font-medium">
                              <Phone size={13} /> {conv.visitor_phone}
                            </span>
                          )}
                          {conv.visitor_email && (
                            <span className="flex items-center gap-1 text-sm text-gray-500">
                              <Mail size={13} /> {conv.visitor_email}
                            </span>
                          )}
                          <span className="flex items-center gap-1 text-xs text-gray-400">
                            <Clock size={12} /> {formatDate(conv.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-full">
                        {conv.messages.length} messages
                      </span>
                      {expanded === conv.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </div>
                  </div>

                  {/* Messages */}
                  {expanded === conv.id && (
                    <div className="border-t bg-gray-50 p-4 space-y-3 max-h-80 overflow-y-auto">
                      {conv.messages.map(msg => (
                        <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[80%] px-3 py-2 rounded-lg text-sm ${
                            msg.role === 'user'
                              ? 'bg-yellow-500 text-white rounded-br-none'
                              : 'bg-white border text-gray-700 rounded-bl-none'
                          }`}>
                            <p>{msg.content}</p>
                            <p className={`text-xs mt-1 ${msg.role === 'user' ? 'text-yellow-100' : 'text-gray-400'}`}>
                              {formatDate(msg.created_at)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default ChatConversations;
